import { createSlice } from '@reduxjs/toolkit';

import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import {
  buildProviderCart,
  clearPurchasedScope,
  getGuestCart,
  getCartCount,
  getCartEntries,
  getCartFromCurrentUser,
  isListingInCart,
  removeCartListing,
  sanitizeCart,
  setCartListing,
  storeGuestCart,
  updateCartListingQuantity,
} from '../util/cart';
import { clearCurrentUser, fetchCurrentUserThunk, setCurrentUser } from './user.duck';

const initialState = {
  cart: {},
  saveCartInProgress: false,
  saveCartError: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCart: (state, action) => {
      state.cart = sanitizeCart(action.payload);
    },
    saveCartStart: state => {
      state.saveCartInProgress = true;
      state.saveCartError = null;
    },
    saveCartSuccess: (state, action) => {
      state.cart = sanitizeCart(action.payload);
      state.saveCartInProgress = false;
      state.saveCartError = null;
    },
    saveCartFailure: (state, action) => {
      state.saveCartInProgress = false;
      state.saveCartError = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        if (action.payload?.id) {
          state.cart = getCartFromCurrentUser(action.payload);
        }
      })
      .addCase(setCurrentUser, (state, action) => {
        if (action.payload?.id) {
          state.cart = getCartFromCurrentUser(action.payload);
        }
      })
      .addCase(clearCurrentUser, state => {
        state.cart = {};
      });
  },
});

export const { setCart, saveCartStart, saveCartSuccess, saveCartFailure } = cartSlice.actions;

const persistCart = nextCart => async (dispatch, getState, sdk) => {
  dispatch(saveCartStart());

  try {
    const currentUser = getState().user.currentUser;
    if (!currentUser?.id) {
      const storedCart = storeGuestCart(nextCart);
      dispatch(saveCartSuccess(storedCart));
      return storedCart;
    }

    const privateData = currentUser?.attributes?.profile?.privateData || {};
    const response = await sdk.currentUser.updateProfile(
      {
        privateData: {
          ...privateData,
          cart: nextCart,
        },
      },
      { expand: true }
    );
    const entities = denormalisedResponseEntities(response);

    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(setCurrentUser(entities[0]));
    dispatch(saveCartSuccess(nextCart));
    return nextCart;
  } catch (e) {
    const error = storableError(e);
    dispatch(saveCartFailure(error));
    throw error;
  }
};

const applyOptimisticCartUpdate = cartUpdater => async (dispatch, getState) => {
  const previousCart = getState().cart.cart;
  const nextCart = sanitizeCart(cartUpdater(previousCart));

  dispatch(setCart(nextCart));

  try {
    await dispatch(persistCart(nextCart));
    return nextCart;
  } catch (e) {
    dispatch(setCart(previousCart));
    throw e;
  }
};

export const replaceCartFromCurrentUser = currentUser => dispatch => {
  dispatch(setCart(getCartFromCurrentUser(currentUser)));
};

export const hydrateGuestCart = () => (dispatch, getState) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser?.id) {
    dispatch(setCart(getGuestCart()));
  }
};

export const addListingToCart = params => dispatch => {
  return dispatch(
    applyOptimisticCartUpdate(cart => {
      return setCartListing(cart, params);
    })
  );
};

export const updateCartListing = params => dispatch => {
  return dispatch(
    applyOptimisticCartUpdate(cart => {
      return updateCartListingQuantity(cart, params);
    })
  );
};

export const removeListingFromCart = params => dispatch => {
  return dispatch(
    applyOptimisticCartUpdate(cart => {
      return removeCartListing(cart, params);
    })
  );
};

export const clearPurchasedCartScope = params => dispatch => {
  return dispatch(
    applyOptimisticCartUpdate(cart => {
      return clearPurchasedScope(cart, params);
    })
  );
};

export const selectCart = state => state.cart.cart;
export const selectCartCount = state => getCartCount(selectCart(state));
export const selectCartEntries = state => getCartEntries(selectCart(state));
export const selectIsListingInCart = (state, sellerId, listingId) =>
  isListingInCart(selectCart(state), sellerId, listingId);
export const selectSaveCartInProgress = state => state.cart.saveCartInProgress;
export const selectSaveCartError = state => state.cart.saveCartError;
export const createProviderCart = entries => buildProviderCart(entries);

export default cartSlice.reducer;
